import { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useMixpanelTrackLink, MIXPANEL_ENABLED } from '../analytics/mixpanel';
import mixpanel from 'mixpanel-browser';

/** A wrapper for url navigation that could be either
 * 1. Internal navigation between pages of this React SPA,
 * 2. External navigation to a different website, or
 * 3. Navigation to the legacy app (02/09/2026: this has been removed having deleted the legacy app, making this component less necessary)
 *
 * Let's avoid using this when possible; this is meant to be a convenience component for links that are currently
 * pointing for the old site or externally, but are hopefully going to be brought into the SPA soon. Using a
 * specific <a> or <Link> tag is preferred when possible.
 */
const FlexibleLink = ({
  href,
  className,
  type = 'internal',
  newTab,
  eventName,
  children,
}: {
  /** The url for navigation. Should be a relative url for internal and legacy nav, and an absolute url for external
   *
   * Relative hrefs should begin with a '/'
   **/
  href: string;
  className?: string;
  type: 'internal' | 'external';
  /** Whether the link opens in a new tab, overriding default behavior. By default, 'external' links open in new tabs */
  newTab?: boolean;
  /** The name of a Mixpanel event fired when this link is clicked */
  eventName?: string;
  children: ReactNode;
}) => {
  const [_, navigate] = useLocation();
  const trackLinkRef = useMixpanelTrackLink<HTMLAnchorElement>(eventName ?? '');
  // TODO: It appears that _not_ setting _blank for 'internal' nav links causes them to request a new page
  // instead of doing an in-SPA nav. Why?
  const target = type !== 'internal' ? '_blank' : undefined;
  const adjustedHref = href;

  if (newTab && type === 'internal') {
    console.warn(
      'Using newTab with internal links is unsupported. Please use type="external" instead.',
    );
  }

  // if (type === 'legacy') {
  //   adjustedHref = `${import.meta.env['VITE_LEGACY_APP_URL']}${href}`;
  // }

  const anchorTag = (
    <a
      ref={type !== 'internal' ? trackLinkRef : undefined}
      className={className}
      href={adjustedHref}
      target={target}
      rel={type === 'external' ? 'noreferrer' : undefined}
      data-linktype={type} // For debugging
    >
      {children}
    </a>
  );

  return (
    <>
      {type === 'internal' && !newTab ?
        <Link
          to={adjustedHref}
          onClick={(e) => {
            // For some reason using Mixpanel here causes full document loads instead of
            // SPA nav, so here we call navigate() manually instead. This defeats the
            // purpose of using <Link>... not sure what's going on here.
            if (eventName) {
              e.preventDefault();
              if (MIXPANEL_ENABLED) {
                mixpanel.track(eventName);
              }
              navigate(adjustedHref);
            }
          }}
          asChild
        >
          {anchorTag}
        </Link>
      : anchorTag}
    </>
  );
};

export default FlexibleLink;
